import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router';
import { routes } from '@/utils/routes/routes';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { getData } from '@/services/api.service';
import { useSelector } from 'react-redux';
import { RootState } from '@/core/redux/store';

const MenuSection = ({ themeData, collections }: any) => {
  const [loading, setLoading] = useState(true);
  const [priceList, setPriceList] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(false);
  
  // Get currency symbol
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );

  // Convert currency code to symbol
  const getCurrencySymbol = (code: string) => {
    if (!code) return '₹'; // default fallback
    
    // If it's already a symbol, return as is
    if (code.length <= 3 && !code.match(/[A-Z]{3}/)) {
      return code;
    }
    
    // Convert common currency codes to symbols
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'Fr',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft',
      'RON': 'lei',
      'BGN': 'лв',
      'HRK': 'kn',
      'RUB': '₽',
      'TRY': '₺',
      'ILS': '₪',
      'THB': '฿',
      'SGD': 'S$',
      'HKD': 'HK$',
      'NZD': 'NZ$',
      'ZAR': 'R',
      'MXN': 'Mex$',
      'PHP': '₱',
      'IDR': 'Rp',
      'MYR': 'RM',
      'VND': '₫',
      'KRW': '₩'
    };
    
    return symbols[code.toUpperCase()] || code;
  };

  const currencySymbol = getCurrencySymbol(currencyCode || '');

  // Detect screen width for responsive tabs
  useEffect(() => {
    const checkScreenWidth = () => {
      setIsMobile(window.innerWidth < 767);
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);

  // Fetch products from API
  useEffect(() => {
    const getPricingList = async () => {
      try {
        setLoading(true);
        const response = await getData(
          `get_all_products?warehouse_id=0&type=laundry`
        );

        if (response.status === 200 || response.status === 201) {
          if (response?.data?.data?.length > 0) {
            setPriceList(response.data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setPriceList([]);
      } finally {
        setLoading(false);
      }
    };

    getPricingList();
  }, []);

  // Get all products from all categories for "All" tab
  const allProducts = useMemo(() => {
    if (!priceList.length) return [];
    return priceList.flatMap((category: any) => 
      (category.product || []).map((product: any) => ({
        ...product,
        categoryName: category.name,
      }))
    );
  }, [priceList]);

  // Get products for active category
  const activeProducts = useMemo(() => {
    if (activeTab === 'all') {
      return allProducts;
    }
    const category = priceList.find((cat: any) => {
      const catKey = cat.name.toLowerCase().replace(/\s+/g, '');
      return catKey === activeTab.toLowerCase().replace(/\s+/g, '');
    });
    return category?.product || [];
  }, [activeTab, priceList, allProducts]);

  // Truncate description to 30 characters
  const truncateDescription = (text: string) => {
    if (!text) return '';
    if (text.length > 30) {
      return text.substring(0, 30) + '...';
    }
    return text;
  };

  // Render tab content (reusable for both Bootstrap and Swiper)
  const renderTabContent = (products: any[]) => {
    if (products.length === 0) {
      return (
        <div className="text-center" style={{ padding: '3rem' }}>
          <p style={{ color: '#45505C' }}>
            {activeTab === 'all' ? 'No products available' : 'No products available in this category'}
          </p>
        </div>
      );
    }

    return (
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className={`menu-card-list ${products.length > 5 ? 'menu-card-list-scrollable' : ''}`}>
            <ul>
              {products.map((item: any, index: number) => (
                <li key={item.id || index} className='d-flex justify-content-between gap-3 align-items-center'>
                  <div className="menu-list-item">
                    <h4>{item.name || 'Menu Item'}</h4>
                    <p>{truncateDescription(item.short_description || item.description || '')}</p>
                  </div>
                  <div className="menu-list-price ms-auto">
                    {currencySymbol}{parseFloat(item.price || 0).toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <section className='sec-bg1-t6'>
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="sec-hdr-t6 text-center">
              {/* <h6 className='fc-2'>Our Menu</h6> */}
              <h3>Pricing List</h3>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            {loading ? (
              <div className="text-center" style={{ padding: '3rem' }}>
                <p style={{ color: '#45505C' }}>Loading menu...</p>
              </div>
            ) : isMobile ? (
              // Swiper Tabs for Mobile (< 767px)
              <div className="menu-swiper-tabs">
                {/* Swiper for Tab Navigation */}
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={10}
                  slidesPerView="auto"
                  navigation={true}
                  className="menu-tabs-swiper"
                >
                  <SwiperSlide 
                    style={{ width: 'auto' }}
                    onClick={() => setActiveTab('all')}
                  >
                    <button className={`menu-tab-button ${activeTab === 'all' ? 'active' : ''}`}>
                      All
                    </button>
                  </SwiperSlide>
                  {priceList.map((category: any, index: number) => {
                    const categoryKey = category.name.toLowerCase().replace(/\s+/g, '');
                    return (
                      <SwiperSlide
                        key={categoryKey || index}
                        style={{ width: 'auto' }}
                        onClick={() => setActiveTab(categoryKey)}
                      >
                        <button className={`menu-tab-button ${activeTab === categoryKey ? 'active' : ''}`}>
                          {category.name}
                        </button>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>

                {/* Tab Content - Rendered based on activeTab */}
                <div className="menu-tab-content-mobile">
                  {renderTabContent(activeProducts)}
                </div>
              </div>
            ) : (
              // Bootstrap Tabs for Desktop (>= 767px)
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k || 'all')}
                id="food-category-tabs"
                className="nav-tabs-t6"
              >
                {/* All Tab */}
                <Tab eventKey="all" title="All">
                  {renderTabContent(activeProducts)}
                </Tab>

                {/* Category Tabs - Dynamically generated from API */}
                {priceList.map((category: any, index: number) => {
                  const categoryKey = category.name.toLowerCase().replace(/\s+/g, '');
                  const categoryProducts = category.product || [];
                  
                  return (
                    <Tab key={categoryKey || index} eventKey={categoryKey} title={category.name}>
                      {renderTabContent(categoryProducts)}
                    </Tab>
                  );
                })}
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default MenuSection;

